// Runtime theming helpers for white-label branding (M1). The Tailwind `primary` token resolves
// `rgb(var(--ds-primary) / <alpha-value>)`, so we set `--ds-primary` to an "R G B" channel triple.

const HEX = /^#([0-9a-fA-F]{6})$/;

/** Converts `#RRGGBB` to a space-separated `R G B` channel triple, or null when malformed. */
export function hexToRgbChannels(hex: string): string | null {
  const match = HEX.exec(hex.trim());
  if (match === null) {
    return null;
  }
  const value = parseInt(match[1]!, 16);
  return `${(value >> 16) & 255} ${(value >> 8) & 255} ${value & 255}`;
}

/** Darkens `#RRGGBB` by a factor (default 0.85) and returns it as a channel triple, for the hover shade. */
export function darkenHexChannels(hex: string, factor = 0.85): string | null {
  const match = HEX.exec(hex.trim());
  if (match === null) {
    return null;
  }
  const value = parseInt(match[1]!, 16);
  const r = Math.round(((value >> 16) & 255) * factor);
  const g = Math.round(((value >> 8) & 255) * factor);
  const b = Math.round((value & 255) * factor);
  return `${r} ${g} ${b}`;
}

/** Applies the tenant primary colour to the document root; no-op for a malformed hex. */
export function applyPrimaryColor(hex: string): void {
  const base = hexToRgbChannels(hex);
  if (base === null) {
    return;
  }
  const hover = darkenHexChannels(hex) ?? base;
  const root = document.documentElement;
  root.style.setProperty('--ds-primary', base);
  root.style.setProperty('--ds-primary-hover', hover);
}

/** Reverts to the CSS-default brand colour (removes the inline overrides). */
export function resetPrimaryColor(): void {
  const root = document.documentElement;
  root.style.removeProperty('--ds-primary');
  root.style.removeProperty('--ds-primary-hover');
}

/**
 * Applies the tenant secondary/accent colour to `--ds-accent`. Falls back to the primary colour
 * when no secondary is set (or it is malformed), so `accent` always resolves to a sensible value.
 */
export function applyAccentColor(secondaryHex: string | null, primaryHex: string): void {
  const channels = (secondaryHex !== null ? hexToRgbChannels(secondaryHex) : null) ?? hexToRgbChannels(primaryHex);
  if (channels === null) {
    return;
  }
  document.documentElement.style.setProperty('--ds-accent', channels);
}

/** Reverts to the CSS-default accent colour. */
export function resetAccentColor(): void {
  document.documentElement.style.removeProperty('--ds-accent');
}

// Build-time favicon href, captured the first time we swap it so a reset can restore it.
let originalFaviconHref: string | null = null;

function faviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (link === null) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

/** Points the browser-tab icon at the tenant favicon, or restores the build-time default when null/blank. */
export function applyFavicon(url: string | null): void {
  const link = faviconLink();
  if (originalFaviconHref === null) {
    originalFaviconHref = link.getAttribute('href');
  }
  if (url !== null && url !== '') {
    link.setAttribute('href', url);
  } else if (originalFaviconHref !== null) {
    link.setAttribute('href', originalFaviconHref);
  }
}

/** Restores the build-time favicon. */
export function resetFavicon(): void {
  if (originalFaviconHref === null) {
    return;
  }
  faviconLink().setAttribute('href', originalFaviconHref);
}
